import { merge } from 'lodash-es';
import * as yup from 'yup';
import { WaterfallTierConfig } from '../data-models/fund-waterfall.data-model';

export const TierFieldToLabel: Record<string, string> = {
  carry: 'Carry',
  gp: 'GP',
  lp: 'LP',
  slp: 'SLP',
  remaining: 'Remaining',
  total: 'Total',
} as const;

export function tierDataFormulaFields() {
  return {
    carry: yup.string().nullable().label(TierFieldToLabel.carry),
    gp: yup.string().nullable().label(TierFieldToLabel.gp),
    lp: yup.string().nullable().label(TierFieldToLabel.lp),
    slp: yup.string().nullable().label(TierFieldToLabel.slp),
  };
}

export function tierDataFormulaSchema() {
  return yup.object().shape(tierDataFormulaFields());
}

export type TierDataFormulas = yup.InferType<ReturnType<typeof tierDataFormulaSchema>>;

export function tierDataConfigFields() {
  return {
    id: yup.string().nullable(),
    // The server's own key for this tier, echoed back on save so it can move the row instead of
    // replacing it. A tier added in the browser has none until the first save returns.
    tierId: yup.string().optional(),
    orderNumber: yup.number().required().default(0),
    tierName: yup.string().nullable().default(''),
    formulas: tierDataFormulaSchema(),
  };
}

export function tierDataConfigSchema() {
  return yup.object().shape(tierDataConfigFields());
}

export type TierDataConfig = yup.InferType<ReturnType<typeof tierDataConfigSchema>>;
export function createTierDataConfig(overrides: Partial<TierDataConfig> = {}): TierDataConfig {
  return { ...tierDataConfigSchema().getDefault(), ...overrides };
}

export function tierConfigViewModelFields() {
  return {
    ...tierDataConfigFields(),
    // BE distinguishes between tiers solely based on fundId and orderNumber - we can't rely on sort order since it's not stable
    // since BE will ignore the id field from the dto, we can generate for the view model
    id: yup.string().required().default(window.crypto.randomUUID()),
  };
}
export function tierConfigViewModelSchema() {
  return yup.object().shape(tierConfigViewModelFields());
}

export type TierConfigViewModel = yup.InferType<ReturnType<typeof tierConfigViewModelSchema>>;

export function createTierConfigViewModel(overrides: Partial<TierConfigViewModel> = {}): TierConfigViewModel {
  return { ...tierConfigViewModelSchema().getDefault(), ...overrides };
}

export function tierConfigToViewModel(
  tierConfig: WaterfallTierConfig,
  overrides: Partial<TierDataConfig | TierConfigViewModel> = {}
): TierConfigViewModel {
  const { id } = overrides;
  return {
    ...tierConfig,
    ...overrides,
    id: typeof id === 'string' ? id : tierConfig.tierId || window.crypto.randomUUID(),
  };
}

export function tierConfigViewModelToTierDataConfig(viewModel: TierConfigViewModel): TierDataConfig {
  const { id, ...rest } = viewModel;
  return {
    ...rest,
  };
}

export function waterfallFormulaSchema() {
  return yup.object({
    formula: yup.string().nullable(),
  });
}
export function distributionSegmentFields() {
  return {
    highlight: yup.boolean().nullable().default(false),
    percentValue: yup.number().required().default(0),
    type: yup.string().oneOf(['lp', 'gp', 'slp', 'carry']).nullable(),
  };
}

export function distributionSegmentSchema() {
  return yup.object().shape(distributionSegmentFields());
}

export type DistributionSegment = yup.InferType<ReturnType<typeof distributionSegmentSchema>>;

export function createDistributionSegment(overrides: Partial<DistributionSegment> = {}): DistributionSegment {
  return merge({}, distributionSegmentSchema().getDefault(), overrides);
}
